import { notFound } from 'next/navigation'
import { env } from './env'
import type { Jurisdiction } from './jurisdiction'

export interface IPath {
  type: string
  name: string
  description?: string
  count?: number
  /** Whether or not the path should be hidden in pages, e.g. /judikatur/bvwg */
  hidden?: boolean
  jurisdiction?: Jurisdiction
}

export const documentTypes = [
  'law',
  'clause',
  'decision',
  'rechtssatz',
  'gazette',
  'draft',
] as const
export type DocumentType = (typeof documentTypes)[number]

export type IPaths = Record<string, IPath>
export type IMapping = Record<
  string,
  {
    name: string
    description?: string
    hidden?: boolean
    paths: IPaths
    documentType: DocumentType[]
  }
>

const pathMappingsEU: IMapping = {
  'eu-recht': {
    name: 'EU-Recht',
    description:
      'Der Bereich EU-Recht beinhaltet Rechtsakte und sonstige veröffentlichte Dokumente der EU.',
    documentType: ['law'],
    paths: {
      stellungnahmen: {
        name: 'Stellungnahmen',
        type: 'EU_A',
        description: 'Stellungnahmen der EU.',
        hidden: true,
      },
      haushaltsplaene: {
        name: 'Haushaltspläne',
        type: 'EU_B',
        description: 'Haushaltspläne der EU.',
        hidden: true,
      },
      erklaerungen: {
        name: 'Erklärungen',
        type: 'EU_C',
        description: 'Erklärungen der EU.',
        hidden: true,
      },
      beschluesse: {
        name: 'Beschlüsse',
        type: 'EU_D',
        description: 'Beschlüsse der EU, mit oder ohne Adressat.',
      },
      gasp: {
        name: 'GASP',
        type: 'EU_E',
        description:
          'Gemeinsame Standpunkte, Gemeinsame Maßnahmen und Gemeinsame Strategien der GASP (Titel V des Vor-Lissabon-Vertrags).',
        hidden: true,
      },
      'polizeiliche-und-justizielle-zusammenarbeit-in-strafsachen': {
        name: 'Polizeiliche und justizielle Zusammenarbeit in Strafsachen',
        type: 'EU_F',
        description:
          'Rechtsakte zur polizeilichen und justiziellen Zusammenarbeit in Strafsachen (Titel VI des Vor-Lissabon-Vertrags).',
        hidden: true,
      },
      entschliessungen: {
        name: 'Entschließungen',
        type: 'EU_G',
        description: 'Entschließungen der EU.',
        hidden: true,
      },
      empfehlungen: {
        name: 'Empfehlungen',
        type: 'EU_H',
        description: 'Empfehlungen der EU.',
        hidden: true,
      },
      'entscheidungen-gemeinschaftsunternehmen': {
        name: 'Entscheidungen zu Gemeinschaftsunternehmen',
        type: 'EU_J',
        description:
          'Entscheidungen, keinen Widerspruch gegen mitgeteilte Gemeinschaftsunternehmen zu erheben.',
        hidden: true,
      },
      'egks-empfehlungen': {
        name: 'EGKS-Empfehlungen',
        type: 'EU_K',
        description:
          'Empfehlungen der Europäischen Gemeinschaft für Kohle und Stahl.',
        hidden: true,
      },
      richtlinien: {
        name: 'Richtlinien',
        type: 'EU_L',
        description: 'Richtlinien der EU.',
      },
      'entscheidungen-unternehmenszusammenschluesse': {
        name: 'Entscheidungen zu Unternehmenszusammenschlüssen',
        type: 'EU_M',
        description:
          'Entscheidungen, keinen Widerspruch gegen mitgeteilte Unternehmenszusammenschlüsse zu erheben.',
        hidden: true,
      },
      'leitlinien-der-ezb': {
        name: 'Leitlinien der EZB',
        type: 'EU_O',
        description: 'Leitlinien der Europäischen Zentralbank.',
        hidden: true,
      },
      'interinstitutionelle-vereinbarungen': {
        name: 'Interinstitutionelle Vereinbarungen',
        type: 'EU_Q',
        description:
          'Interinstitutionelle Vereinbarungen, Geschäftsordnungen und interne Rechtsakte.',
        hidden: true,
      },
      verordnungen: {
        name: 'Verordnungen',
        type: 'EU_R',
        description: 'Verordnungen der EU.',
      },
      'allgemeine-egks-entscheidungen': {
        name: 'Allgemeine EGKS-Entscheidungen',
        type: 'EU_S',
        description: 'Allgemeine Entscheidungen der EGKS.',
        hidden: true,
      },
      'sonstige-dokumente-reihe-l': {
        name: 'Sonstige Dokumente (Reihe L)',
        type: 'EU_X',
        description:
          'Sonstige in der ABl.-Reihe L oder vor 1967 veröffentlichte Dokumente.',
        hidden: true,
      },
      'sonstige-dokumente-reihe-c': {
        name: 'Sonstige Dokumente (Reihe C)',
        type: 'EU_Y',
        description: 'Sonstige in der ABl.-Reihe C veröffentlichte Dokumente.',
        hidden: true,
      },
    },
  },
  'eu-judikatur': {
    name: 'EU-Judikatur',
    description:
      'Der Bereich EU-Judikatur beinhaltet Entscheidungen von verschiedenen Gerichten und Behörden der EU.',
    documentType: ['decision'],
    paths: {
      gerichtshof: {
        name: 'Gerichtshof',
        type: 'Gerichtshof',
        count: 0,
      },
      gericht: {
        name: 'Gericht',
        type: 'Gericht',
        count: 0,
      },
      'gericht-fuer-den-oeffentlichen-dienst': {
        name: 'Gericht für den öffentlichen Dienst',
        type: 'Gericht für den öffentlichen Dienst',
        count: 0,
      },
    },
  },
}

// Descriptions based on feedback from Marco and Michi, see https://www.notion.so/gesetzefindenat/Enhancement-Improve-explain-text-for-E-R-Judikatur-Justiz-1bfadbc8c180450190752081455689eb
/** URL path mappings */
export const pathMappingsAT: IMapping = {
  bundesrecht: {
    name: 'Bundesrecht',
    description:
      'Ins Bundesrecht fallen Gesetze, Verordnungen, Kundmachungen und Staatsverträge, die allgemein in ganz Österreich gelten. Dies unterscheidet es vom Landesrecht, welches nur für das jeweilige Bundesland gilt.',
    documentType: ['law'],
    paths: {
      bundesverfassungsgesetze: {
        type: 'BVG', // in future lawCategory
        name: 'Bundesverfassungsgesetze',
        description:
          'Von mindestens 2/3 der anwesenden Parlamentsabgeordneten beschlossene Gesetze, wobei mindestens 1/2 aller Abgeordneten anwesend sein müssen.',
      },
      bundesgesetze: {
        type: 'BG',
        name: 'Bundesgesetze',
        description:
          'Von 50% + 1 Stimme der anwesenden Parlamentsabgeordneten beschlossene Gesetze, wobei mindestens 1/3 aller Abgeordneten anwesend sein müssen.',
      },
      verordnungen: {
        type: 'V',
        name: 'Verordnungen',
        description:
          'Eine von einer Verwaltungsbehörde, Bundesregierung oder einem Bundesminister erlassene allgemeine Rechtsnorm, die gesetzesgleiche Wirkung hat.',
      },
      vertraege: {
        type: 'Vertrag',
        name: 'Internationale Verträge',
        description:
          'Internationale Verträge mit Staaten und/oder internationalen Organisationen.',
      },
      kundmachungen: {
        type: 'K',
        name: 'Kundmachungen',
        description:
          'Kundmachungen zu Bundesgesetzen, Staatsverträgen und Verordnungen.',
      },
      vereinbarungen: {
        type: 'Vereinbarung',
        name: 'Art. 15a Vereinbarungen',
        description:
          'Vereinbarung zwischen dem Bund und den Bundesländern (gemäß Art. 15a B-VG).',
      },
      geschaeftsordnungen: {
        type: 'Geschäftsordnung',
        name: 'Geschäftsordnungen',
        description:
          'Regelungen von organisatorischen Abläufen und von Rechten/Pflichten der Abgeordneten.',
      },
      entschliessungen: {
        type: 'Entschließung',
        name: 'Entschließungen des Bundespräsidenten',
        description:
          'Rechtsakte des Bundespräsidenten (zB. die Ernennung der Bundesregierung).',
      },
      bekanntmachungen: {
        type: 'Bekanntmachung',
        name: 'Bekanntmachungen',
        description: '',
      },
      verfuegungen: {
        type: 'Verfügung',
        name: 'Verfügungen',
        description: '',
      },
      bundesgesetzblaetter: {
        type: 'gazette',
        name: 'Bundesgesetzblätter',
        description: '',
      },
    },
  },
  landesrecht: {
    name: 'Landesrecht',
    description:
      'Ins Landesrecht fallen Gesetze, Verordnungen und Kundmachungen, die für ein jeweiliges Bundesland gelten. Dies unterscheidet es vom Bundesrecht, welches Österreichweit gilt.',
    documentType: ['law'],
    paths: {
      landesverfassungsgesetze: {
        type: 'L_LVG', // in future lawCategory
        name: 'Landesverfassungsgesetze',
        // description: 'Von mindestens 2/3 der anwesenden Parlamentsabgeordneten beschlossene Gesetze, wobei mindestens 1/2 aller Abgeordneten anwesend sein müssen.',
      },
      landesgesetze: {
        type: 'L_LG',
        name: 'Landesesetze',
        // description: 'Von 50% + 1 Stimme der anwesenden Parlamentsabgeordneten beschlossene Gesetze, wobei mindestens 1/3 aller Abgeordneten anwesend sein müssen.',
      },
      verordnungen: {
        type: 'L_V',
        name: 'Verordnungen',
        // description: 'Eine von einer Verwaltungsbehörde, Bundesregierung oder einem Bundesminister erlassene allgemeine Rechtsnorm, die gesetzesgleiche Wirkung hat.',
      },
      kundmachungen: {
        type: 'L_K',
        name: 'Kundmachungen',
        // description: 'Kundmachungen zu Bundesgesetzen, Staatsverträgen und Verordnungen.',
      },
      vereinbarungen: {
        type: 'L_Vereinbarung',
        name: 'Art. 15a Vereinbarungen',
        // description: 'Vereinbarung zwischen dem Bund und den Bundesländern (gemäß Art. 15a B-VG).',
      },
      beschluesse: {
        type: 'L_Beschluss',
        name: 'Beschlüsse',
        hidden: true,
      },
      L_s: {
        type: 'L_s',
        name: '',
        hidden: true,
      },
      sonstiges: {
        type: 'L_Sonstiges',
        name: 'Sonstiges',
        hidden: true,
      },
      'ris-information': {
        type: 'L_RIS-Information',
        name: 'RIS-Information',
        hidden: true,
      },
      landtagsbeschluss: {
        type: 'L_Landtagsbeschluss',
        name: 'Landtagsbeschluss',
        hidden: true,
      },
      landesgesetzblaetter: {
        type: 'L_gazette',
        hidden: true,
        name: 'Landesgesetzblätter',
        description: '',
      },
    },
  },
  judikatur: {
    name: 'Judikatur',
    description:
      'Der Bereich Judikatur beinhaltet Entscheidungen und Rechtssätze von verschiedenen Gerichten und Behörden.',
    documentType: ['rechtssatz', 'decision'],
    paths: {
      justiz: {
        name: 'Justiz',
        type: 'Justiz', // in future category?
        description:
          'Entscheidungen und Rechtssätze von folgenden Gerichten: Oberster Gerichtshof (OGH), Oberlandesgerichte (OLG), Landesgerichte (LG), Bezirksgerichte (BG), Oberster Patent- und Markensenat (OPMS bis Ende 2013) sowie ausländische Entscheidungen (AUSL).',
        hidden: true,
        count: 0,
      },
      vwgh: {
        name: 'VwGH',
        type: 'VwGH',
        count: 481852,
      },
      ogh: {
        name: 'OGH',
        type: 'OGH',
        count: 276301,
      },
      bvwg: {
        name: 'BVwG',
        type: 'BVwG',
        count: 248554,
      },
      vfgh: {
        name: 'VfGH',
        type: 'VfGH',
        count: 48502,
      },
      bfg: {
        name: 'BFG',
        type: 'BFG',
        count: 34649,
      },
      'ausl-egmr': {
        name: 'AUSL EGMR',
        type: 'AUSL EGMR',
        count: 4127,
      },
      'olg-wien': {
        name: 'OLG Wien',
        type: 'OLG Wien',
        count: 2743,
      },
      dsb: {
        name: 'DSB',
        type: 'DSB',
        count: 1725,
      },
      'ausl-bgh': {
        name: 'AUSL BGH',
        type: 'AUSL BGH',
        count: 809,
      },
      'olg-innsbruck': {
        name: 'OLG Innsbruck',
        type: 'OLG Innsbruck',
        count: 677,
      },
      'olg-linz': {
        name: 'OLG Linz',
        type: 'OLG Linz',
        count: 528,
      },
      'olg-graz': {
        name: 'OLG Graz',
        type: 'OLG Graz',
        count: 486,
      },
      'lg-fur-zrs-wien': {
        name: 'LG für ZRS Wien',
        type: 'LG für ZRS Wien',
        count: 404,
      },
      'lg-eisenstadt': {
        name: 'LG Eisenstadt',
        type: 'LG Eisenstadt',
        count: 312,
      },
      'ausl-bag': {
        name: 'AUSL BAG',
        type: 'AUSL BAG',
        count: 308,
      },
      'lg-feldkirch': {
        name: 'LG Feldkirch',
        type: 'LG Feldkirch',
        count: 306,
      },
      'lg-klagenfurt': {
        name: 'LG Klagenfurt',
        type: 'LG Klagenfurt',
        count: 229,
      },
      'ausl-ekmr': {
        name: 'AUSL EKMR',
        type: 'AUSL EKMR',
        count: 198,
      },
      rg: {
        name: 'RG',
        type: 'RG',
        count: 186,
      },
      'lg-korneuburg': {
        name: 'LG Korneuburg',
        type: 'LG Korneuburg',
        count: 176,
      },
      'lg-st-polten': {
        name: 'LG St. Pölten',
        type: 'LG St. Pölten',
        count: 172,
      },
      'ausl-eugh': {
        name: 'AUSL EUGH',
        type: 'AUSL EUGH',
        count: 8,
      },
      'handelsgericht-wien': {
        name: 'Handelsgericht Wien',
        type: 'Handelsgericht Wien',
        count: 144,
      },
      opms: {
        name: 'OPMS',
        type: 'OPMS',
        count: 115,
      },
      'lg-salzburg': {
        name: 'LG Salzburg',
        type: 'LG Salzburg',
        count: 107,
      },
      opm: {
        name: 'OPM',
        type: 'OPM',
        count: 106,
      },
      'lg-fur-zrs-graz': {
        name: 'LG für ZRS Graz',
        type: 'LG für ZRS Graz',
        count: 103,
      },
      'lg-ried-innkreis': {
        name: 'LG Ried/Innkreis',
        type: 'LG Ried/Innkreis',
        count: 100,
      },
      ausl: {
        name: 'AUSL',
        type: 'AUSL',
        count: 87,
      },
      son: {
        name: 'SON',
        type: 'SON',
        count: 74,
      },
      'lg-leoben': {
        name: 'LG Leoben',
        type: 'LG Leoben',
        count: 74,
      },
      pgh: {
        name: 'PGH',
        type: 'PGH',
        count: 69,
      },
      'lg-wels': {
        name: 'LG Wels',
        type: 'LG Wels',
        count: 64,
      },
      'lg-krems-donau': {
        name: 'LG Krems/Donau',
        type: 'LG Krems/Donau',
        count: 64,
      },
      'lg-wr-neustadt': {
        name: 'LG Wr. Neustadt',
        type: 'LG Wr. Neustadt',
        count: 52,
      },
      'lg-linz': {
        name: 'LG LINZ',
        type: 'LG LINZ',
        count: 2,
      },
      'ausl-bsg': {
        name: 'AUSL BSG',
        type: 'AUSL BSG',
        count: 47,
      },
      'arbeits--und-sozialgericht-wien': {
        name: 'Arbeits- und Sozialgericht Wien',
        type: 'Arbeits- und Sozialgericht Wien',
        count: 46,
      },
      'ausl-kassationsgerichtshof-frankreich---cour-de-cassation': {
        name: 'AUSL Kassationsgerichtshof Frankreich - Cour de Cassation',
        type: 'AUSL Kassationsgerichtshof Frankreich - Cour de Cassation',
        count: 18,
      },
      'ausl-bayolg': {
        name: 'AUSL BayOLG',
        type: 'AUSL BayOLG',
        count: 15,
      },
      'lg-innsbruck': {
        name: 'LG Innsbruck',
        type: 'LG Innsbruck',
        count: 14,
      },
      'ausl-olg-celle': {
        name: 'AUSL OLG Celle',
        type: 'AUSL OLG Celle',
        count: 14,
      },
      'lg-fur-strafsachen-wien': {
        name: 'LG für Strafsachen Wien',
        type: 'LG für Strafsachen Wien',
        count: 14,
      },
      'ausl-olg-hamburg': {
        name: 'AUSL OLG Hamburg',
        type: 'AUSL OLG Hamburg',
        count: 13,
      },
      'lg-steyr': {
        name: 'LG Steyr',
        type: 'LG Steyr',
        count: 11,
      },
      'ausl-bverfg': {
        name: 'AUSL BVerfG',
        type: 'AUSL BVerfG',
        count: 11,
      },
      'ausl-olg-dusseldorf': {
        name: 'AUSL OLG Düsseldorf',
        type: 'AUSL OLG Düsseldorf',
        count: 11,
      },
      'lg-fur-strafsachen-graz': {
        name: 'LG für Strafsachen Graz',
        type: 'LG für Strafsachen Graz',
        count: 11,
      },
      'ausl-olg-hamm': {
        name: 'AUSL OLG Hamm',
        type: 'AUSL OLG Hamm',
        count: 10,
      },
      'ausl-olg-koln': {
        name: 'AUSL OLG Köln',
        type: 'AUSL OLG Köln',
        count: 10,
      },
      'ausl-olg-stuttgart': {
        name: 'AUSL OLG Stuttgart',
        type: 'AUSL OLG Stuttgart',
        count: 10,
      },
      'ausl-oberstes-gericht-italien---corte-suprema-di-cassazione': {
        name: 'AUSL Oberstes Gericht Italien - Corte Suprema di Cassazione',
        type: 'AUSL Oberstes Gericht Italien - Corte Suprema di Cassazione',
        count: 10,
      },
      'ausl-olg-munchen': {
        name: 'AUSL OLG München',
        type: 'AUSL OLG München',
        count: 8,
      },
      bsg: {
        name: 'BSG',
        type: 'BSG',
        count: 7,
      },
      'ausl-olg-frankfurt': {
        name: 'AUSL OLG Frankfurt',
        type: 'AUSL OLG Frankfurt',
        count: 6,
      },
      bgh: {
        name: 'BGH',
        type: 'BGH',
        count: 5,
      },
      'bg-neusiedl': {
        name: 'BG Neusiedl',
        type: 'BG Neusiedl',
        count: 5,
      },
      'ausl-olg-karlsruhe': {
        name: 'AUSL OLG Karlsruhe',
        type: 'AUSL OLG Karlsruhe',
        count: 5,
      },
      'ausl-olg-braunschweig': {
        name: 'AUSL OLG Braunschweig',
        type: 'AUSL OLG Braunschweig',
        count: 5,
      },
      'bg-melk': {
        name: 'BG Melk',
        type: 'BG Melk',
        count: 4,
      },
      'lg-hg-wien': {
        name: 'LG HG Wien',
        type: 'LG HG Wien',
        count: 4,
      },
      'ausl-appellationsgericht-england---court-of-appeal': {
        name: 'AUSL Appellationsgericht England - Court of Appeal',
        type: 'AUSL Appellationsgericht England - Court of Appeal',
        count: 4,
      },
      'ausl-bfh': {
        name: 'AUSL BFH',
        type: 'AUSL BFH',
        count: 3,
      },
      'ausl-lg-berlin': {
        name: 'AUSL LG Berlin',
        type: 'AUSL LG Berlin',
        count: 3,
      },
      'ausl-olg-oldenburg': {
        name: 'AUSL OLG Oldenburg',
        type: 'AUSL OLG Oldenburg',
        count: 3,
      },
      'ausl-olg-saarbrucken': {
        name: 'AUSL OLG Saarbrücken',
        type: 'AUSL OLG Saarbrücken',
        count: 3,
      },
      egmr: {
        name: 'EGMR',
        type: 'EGMR',
        count: 2,
      },
      'ausl-olg-koblenz': {
        name: 'AUSL OLG Koblenz',
        type: 'AUSL OLG Koblenz',
        count: 2,
      },
      'ausl-olg-bamberg': {
        name: 'AUSL OLG Bamberg',
        type: 'AUSL OLG Bamberg',
        count: 2,
      },
      'ausl-ag-darmstadt': {
        name: 'AUSL AG Darmstadt',
        type: 'AUSL AG Darmstadt',
        count: 2,
      },
      'ausl-lg-dusseldorf': {
        name: 'AUSL LG Düsseldorf',
        type: 'AUSL LG Düsseldorf',
        count: 2,
      },
      'ausl-kg-berlin---west': {
        name: 'AUSL KG Berlin - West',
        type: 'AUSL KG Berlin - West',
        count: 2,
      },
      'ausl-lsg-nordrhein-westfalen': {
        name: 'AUSL LSG Nordrhein-Westfalen',
        type: 'AUSL LSG Nordrhein-Westfalen',
        count: 2,
      },
      'ausl-hanseatisches-olg-bremen': {
        name: 'AUSL Hanseatisches OLG Bremen',
        type: 'AUSL Hanseatisches OLG Bremen',
        count: 2,
      },
      'arbeits-und-sozialgericht-wien': {
        name: 'Arbeits und Sozialgericht Wien',
        type: 'Arbeits und Sozialgericht Wien',
        count: 2,
      },
      'ausl-oberstes-gericht-norwegen---hoyesterett': {
        name: 'AUSL Oberstes Gericht Norwegen - Hoyesterett',
        type: 'AUSL Oberstes Gericht Norwegen - Hoyesterett',
        count: 2,
      },
      'ausl-oberstes-gericht-irland---supreme-court': {
        name: 'AUSL Oberstes Gericht Irland - Supreme Court',
        type: 'AUSL Oberstes Gericht Irland - Supreme Court',
        count: 2,
      },
      'ausl-high-court-of-justice---chancery-division': {
        name: 'AUSL High Court of Justice - Chancery Division',
        type: 'AUSL High Court of Justice - Chancery Division',
        count: 2,
      },
      'ausl-court-of-customs-and-patent-appeals---england': {
        name: 'AUSL Court of Customs and Patent Appeals - England',
        type: 'AUSL Court of Customs and Patent Appeals - England',
        count: 2,
      },
      eugh: {
        name: 'EuGH',
        type: 'EuGH',
        count: 1,
      },
      baylsg: {
        name: 'BayLSG',
        type: 'BayLSG',
        count: 1,
      },
      'ausl-lsg': {
        name: 'AUSL LSG',
        type: 'AUSL LSG',
        count: 1,
      },
      'ausl-emgr': {
        name: 'AUSL EMGR',
        type: 'AUSL EMGR',
        count: 1,
      },
      'ausl-egmrh': {
        name: 'AUSL EGMRH',
        type: 'AUSL EGMRH',
        count: 1,
      },
      'olg-koblenz': {
        name: 'OLG Koblenz',
        type: 'OLG Koblenz',
        count: 1,
      },
      'ausl-bverwg': {
        name: 'AUSL BVerwG',
        type: 'AUSL BVerwG',
        count: 1,
      },
      'ausl-lg-kiel': {
        name: 'AUSL LG Kiel',
        type: 'AUSL LG Kiel',
        count: 1,
      },
      'ausl-lg-bonn': {
        name: 'AUSL LG Bonn',
        type: 'AUSL LG Bonn',
        count: 1,
      },
      'ausl-lg-koln': {
        name: 'AUSL LG Köln',
        type: 'AUSL LG Köln',
        count: 1,
      },
      'ausl-bgh-koln': {
        name: 'AUSL BGH Köln',
        type: 'AUSL BGH Köln',
        count: 1,
      },
      'ausl-lg-lubeck': {
        name: 'AUSL LG Lübeck',
        type: 'AUSL LG Lübeck',
        count: 1,
      },
      'ausl-lg-koblenz': {
        name: 'AUSL LG Koblenz',
        type: 'AUSL LG Koblenz',
        count: 1,
      },
      'ausl-lg-hamburg': {
        name: 'AUSL LG Hamburg',
        type: 'AUSL LG Hamburg',
        count: 1,
      },
      'ausl-lg-ansbach': {
        name: 'AUSL LG Ansbach',
        type: 'AUSL LG Ansbach',
        count: 1,
      },
      'ausl-bg-leipzig': {
        name: 'AUSL BG Leipzig',
        type: 'AUSL BG Leipzig',
        count: 1,
      },
      'ausl-ag-giessen': {
        name: 'AUSL AG Gießen',
        type: 'AUSL AG Gießen',
        count: 1,
      },
      'ausl-verwg-koln': {
        name: 'AUSL VerwG Köln',
        type: 'AUSL VerwG Köln',
        count: 1,
      },
      'ausl-lg-munster': {
        name: 'AUSL LG Münster',
        type: 'AUSL LG Münster',
        count: 1,
      },
      'ausl-lg-hannover': {
        name: 'AUSL LG Hannover',
        type: 'AUSL LG Hannover',
        count: 1,
      },
      'ausl-lg-duisburg': {
        name: 'AUSL LG Duisburg',
        type: 'AUSL LG Duisburg',
        count: 1,
      },
      'ausl-lag-hamburg': {
        name: 'AUSL LAG Hamburg',
        type: 'AUSL LAG Hamburg',
        count: 1,
      },
      'ausl-sg-wurzburg': {
        name: 'AUSL SG Würzburg',
        type: 'AUSL SG Würzburg',
        count: 1,
      },
      'ausl-olg-neustadt': {
        name: 'AUSL OLG Neustadt',
        type: 'AUSL OLG Neustadt',
        count: 1,
      },
      'ausl-olg-freiburg': {
        name: 'AUSL OLG Freiburg',
        type: 'AUSL OLG Freiburg',
        count: 1,
      },
      'ausl-lg-stuttgart': {
        name: 'AUSL LG Stuttgart',
        type: 'AUSL LG Stuttgart',
        count: 1,
      },
      'ausl-lg-offenburg': {
        name: 'AUSL LG Offenburg',
        type: 'AUSL LG Offenburg',
        count: 1,
      },
      'ausl-lg-flensburg': {
        name: 'AUSL LG Flensburg',
        type: 'AUSL LG Flensburg',
        count: 1,
      },
      'ausl-lg-bielefeld': {
        name: 'AUSL LG Bielefeld',
        type: 'AUSL LG Bielefeld',
        count: 1,
      },
      'ausl-ag-landstuhl': {
        name: 'AUSL AG Landstuhl',
        type: 'AUSL AG Landstuhl',
        count: 1,
      },
      'ausl-olg-tubingen': {
        name: 'AUSL OLG Tübingen',
        type: 'AUSL OLG Tübingen',
        count: 1,
      },
      'ausl-olg-schleswig': {
        name: 'AUSL OLG Schleswig',
        type: 'AUSL OLG Schleswig',
        count: 1,
      },
      'ausl-olg-nurnberg': {
        name: 'AUSL OLG Nürnberg',
        type: 'AUSL OLG Nürnberg',
        count: 1,
      },
      'ausl-lg-ravensburg': {
        name: 'AUSL LG Ravensburg',
        type: 'AUSL LG Ravensburg',
        count: 1,
      },
      'ausl-lg-munchen-i': {
        name: 'AUSL LG München I',
        type: 'AUSL LG München I',
        count: 1,
      },
      'ausl-lg-hildesheim': {
        name: 'AUSL LG Hildesheim',
        type: 'AUSL LG Hildesheim',
        count: 1,
      },
      'ausl-lag-stuttgart': {
        name: 'AUSL LAG Stuttgart',
        type: 'AUSL LAG Stuttgart',
        count: 1,
      },
      'ausl-kammerg-berlin': {
        name: 'AUSL KammerG Berlin',
        type: 'AUSL KammerG Berlin',
        count: 1,
      },
      'ausl-lg-saarbrucken': {
        name: 'AUSL LG Saarbrücken',
        type: 'AUSL LG Saarbrücken',
        count: 1,
      },
      'ausl-lg-braunschweig': {
        name: 'AUSL LG Braunschweig',
        type: 'AUSL LG Braunschweig',
        count: 1,
      },
      'ausl-lg-baden-freiburg': {
        name: 'AUSL LG Baden Freiburg',
        type: 'AUSL LG Baden Freiburg',
        count: 1,
      },
      'ausl-lg-nurnberg-furth': {
        name: 'AUSL LG Nürnberg-Fürth',
        type: 'AUSL LG Nürnberg-Fürth',
        count: 1,
      },
      'ausl-lg-monchengladbach': {
        name: 'AUSL LG Mönchengladbach',
        type: 'AUSL LG Mönchengladbach',
        count: 1,
      },
      'ausl-kantonsgericht-tessin': {
        name: 'AUSL Kantonsgericht Tessin',
        type: 'AUSL Kantonsgericht Tessin',
        count: 1,
      },
      'ausl-bundesgericht-schweiz': {
        name: 'AUSL Bundesgericht Schweiz',
        type: 'AUSL Bundesgericht Schweiz',
        count: 1,
      },
      'arbeits--und-sozailgericht-wien': {
        name: 'Arbeits- und Sozailgericht Wien',
        type: 'Arbeits- und Sozailgericht Wien',
        count: 1,
      },
      'ausl-oberstes-gericht-danemark': {
        name: 'AUSL Oberstes Gericht Dänemark',
        type: 'AUSL Oberstes Gericht Dänemark',
        count: 1,
      },
      'ausl-oberstes-gericht---supreme-court': {
        name: 'AUSL Oberstes Gericht - Supreme Court',
        type: 'AUSL Oberstes Gericht - Supreme Court',
        count: 1,
      },
      'ausl-appellationsgericht-england---court-of-appeals': {
        name: 'AUSL Appellationsgericht England - Court of Appeals',
        type: 'AUSL Appellationsgericht England - Court of Appeals',
        count: 1,
      },
      'ausl-appellationsgerichtshof-paris---cour-dappel-paris': {
        name: "AUSL Appellationsgerichtshof Paris - Cour d'Appel Paris",
        type: "AUSL Appellationsgerichtshof Paris - Cour d'Appel Paris",
        count: 1,
      },
    },
  },
  // gemeinderecht: {}
  ...pathMappingsEU,
}
export const pathMappingsDE: IMapping = {
  bundesrecht: {
    name: 'Bundesrecht',
    description:
      'Ins Bundesrecht fallen Gesetze, Verordnungen, Kundmachungen und Staatsverträge, die allgemein in ganz Deutschland gelten. Dies unterscheidet es vom Landesrecht, welches nur für das jeweilige Bundesland gilt.',
    documentType: ['law', 'gazette'],
    paths: {
      grundgesetz: {
        type: 'GG',
        name: 'Grundgesetz',
        jurisdiction: 'de',
      },
      bundesgesetze: {
        type: 'BG',
        name: 'Bundesgesetze',
        jurisdiction: 'de',
      },
      rechtsverordnungen: {
        type: 'RV',
        name: 'Rechtsverordnungen',
        jurisdiction: 'de',
      },
      satzungen: {
        type: 'SATZUNG',
        name: 'Satzungen',
        jurisdiction: 'de',
      },
      verwaltungsvorschriften: {
        type: 'VVW',
        name: 'Verwaltungsvorschriften',
      },
      bekanntmachungen: {
        type: 'BEK',
        name: 'Bekanntmachungen',
        jurisdiction: 'de',
      },
      neubekanntmachungen: {
        type: 'NEUBEK',
        name: 'Neubekanntmachungen',
        jurisdiction: 'de',
      },
      bundesgesetzblaetter: {
        type: 'gazette',
        name: 'Bundesgesetzblätter',
        jurisdiction: 'de',
      },
      vertraege: {
        type: 'VERTRAG',
        name: 'Verträge',
        jurisdiction: 'de',
      },
    },
  },
  ...pathMappingsEU,
}

export const pathMappings =
  env.NEXT_PUBLIC_DEFAULT_JURISDICTION === 'de'
    ? pathMappingsDE
    : pathMappingsAT

export type TypeMappings = Record<
  string,
  IPath & { category: string; subCategory: string }
>

function buildTypeMappings(mapping: IMapping): TypeMappings {
  const result: TypeMappings = {}
  Object.entries(mapping).forEach(([key, item]) => {
    Object.entries(item.paths).forEach(([_key, _item]) => {
      // Lowercase the key to work around inconsistent casing in the data
      // See https://www.notion.so/gesetzefindenat/Bug-auf-prod-crash-in-search-5a698f5ae20f4e14918da779a534f993?pvs=4
      result[_item.type.toLowerCase()] = {
        ..._item,
        category: key,
        subCategory: _key,
      }
    })
  })
  return result
}

export const typeMappingsAT = buildTypeMappings(pathMappingsAT)
export const typeMappingsDE = buildTypeMappings(pathMappingsDE)

/**
 * lawType mappings for *this deployment's* jurisdiction. Cross-jurisdiction
 * links (the dashboard serves AT and DE laws side by side) must go through
 * {@link typeMappingsFor} instead — an AT-only table has no entry for a DE
 * lawType, and `getUrl` throws on the miss.
 */
export const typeMappings: TypeMappings =
  env.NEXT_PUBLIC_DEFAULT_JURISDICTION === 'de'
    ? typeMappingsDE
    : typeMappingsAT

/**
 * The lawType table that owns `jurisdiction`. EU norms are published under both
 * GesetzeFinden sites, so they stay on the local deployment's table.
 */
export function typeMappingsFor(
  jurisdiction: Jurisdiction | null | undefined,
): TypeMappings {
  if (jurisdiction === 'de') return typeMappingsDE
  if (jurisdiction === 'at') return typeMappingsAT
  return typeMappings
}

const words = {
  bundesgesetze: 'Bundesgesetz',
  bundesverfassungsgesetze: 'Verfassungsgesetz',
  verordnungen: 'Verordnung',
  verträge: 'Vertrag',
  kundmachungen: 'Kundmachung',
  vereinbarungen: 'Vereinbarung',
  geschäftsordnungen: 'Geschäftsordnung',
  entschließungen: 'Entschließung',
  entscheidungen: 'Entscheidung',
  rechtssätze: 'Rechtssatz',
} as const

export function singular(text: string): string | undefined {
  return words[text.toLowerCase() as keyof typeof words]
}

function isValidCategory(
  category: string,
): category is keyof typeof pathMappings {
  return (
    category in pathMappings &&
    pathMappings[category as keyof typeof pathMappings]?.hidden !== true
  )
}

export function getCategory(category: string) {
  if (!isValidCategory(category)) {
    notFound()
  }

  return pathMappings[category]
}

export const descriptionMappings = {
  gesetze: {
    10011336:
      'Die Straßenverkehrsordnung (STVO) von 1960 ist ein österreichisches Bundesgesetz, das die Regeln und Vorschriften für den Straßenverkehr in Österreich festlegt. Das Gesetz regelt Aspekte wie Verkehrsregeln, Verkehrszeichen, Vorfahrtsregeln, Geschwindigkeitsbegrenzungen und Verhaltensweisen im Straßenverkehr.',
    10002296:
      'Das Strafgesetzbuch (StGB) ist ein österreichisches Bundesgesetz, das strafbare Handlungen definiert und die Strafen dafür festlegt. Es regelt Verbrechen wie Diebstahl, Körperverletzung, Mord und Betrug. Das StGB bildet die rechtliche Grundlage für die strafrechtliche Verfolgung von Straftaten.',
    10001622:
      'Das Allgemeine Bürgerliche Gesetzbuch (ABGB) ist ein österreichisches Bundesgesetz, das das Zivilrecht regelt. Es umfasst Bestimmungen zu Verträgen, Eigentum, Erbrecht, Familienrecht und Schadenersatz. Das ABGB bildet die rechtliche Grundlage für das Zusammenleben und den Rechtsverkehr.',
    10011040:
      'Das Suchtmittelgesetz (SMG) ist ein österreichisches Bundesgesetz, das den Umgang mit Suchtmitteln wie Drogen regelt. Es verbietet den Besitz, Handel und Konsum von illegalen Substanzen und legt Maßnahmen zur Suchtprävention und -bekämpfung fest. Das SMG hat das Ziel, die öffentliche Gesundheit zu schützen und den Missbrauch von Suchtmitteln einzudämmen.',
    10002531:
      'Das Mietrechtsgesetz (MRG) ist ein österreichisches Bundesgesetz, das die Rechte und Pflichten von Mietern und Vermietern regelt. Es betrifft Mietverträge, Mietzins, Kündigungsschutz und Instandhaltung von Mietobjekten. Das MRG zielt darauf ab, den Mieterschutz zu gewährleisten und ein faires Mietverhältnis sicherzustellen.',
    10001700:
      'Die Exekutionsordnung (EO) ist ein österreichisches Gesetz, das die Regelungen für die Zwangsvollstreckung von Geldforderungen und anderen Vollstreckungstiteln festlegt. Sie regelt Verfahren wie Pfändungen und Zwangsversteigerungen. Die EO dient dazu, Gläubigern bei der Durchsetzung ihrer Ansprüche zu helfen und Schuldner zur Begleichung ihrer Schulden zu bewegen.',
    10002326:
      'Die Strafprozessordnung (StPO) ist ein österreichisches Gesetz, das die rechtlichen Grundlagen für strafrechtliche Ermittlungs- und Gerichtsverfahren festlegt. Sie regelt Verfahrensabläufe, Befugnisse der Strafverfolgungsbehörden und Rechte der Beschuldigten. Die StPO zielt darauf ab, ein faires und rechtsstaatliches Verfahren zu gewährleisten.',
    10001702:
      'Das Unternehmensgesetzbuch (UGB) ist ein österreichisches Gesetz, das die rechtlichen Regelungen für Unternehmen festlegt. Es betrifft Aspekte wie Gründung, Organisation, Haftung, Buchführung und Jahresabschluss. Das UGB dient dazu, einen rechtlichen Rahmen für die Geschäftstätigkeit von Unternehmen zu schaffen und den Schutz von Gläubigern und Gesellschaftern sicherzustellen.',
    10004570:
      'Das Einkommensteuergesetz (EStG) von 1988 ist ein österreichisches Gesetz, das die Besteuerung von Einkommen regelt. Es betrifft die Ermittlung des steuerpflichtigen Einkommens, die Berechnung der Einkommensteuer und Abzugsmöglichkeiten. Das ESTG umfasst Regelungen zu verschiedenen Einkunftsarten wie unselbständiger Tätigkeit, Kapitalvermögen und Vermietung.',
    10001699:
      'Die Zivilprozessordnung (ZPO) ist ein österreichisches Gesetz, das die Regelungen für Zivilverfahren vor Gericht festlegt. Sie betrifft Klageverfahren, Beweisführung, Urteilsverkündung und Vollstreckung zivilrechtlicher Ansprüche. Die ZPO zielt darauf ab, einen fairen und effizienten Ablauf von Zivilprozessen zu gewährleisten.',
    10011384:
      'Das Kraftfahrgesetz (KFG) von 1967 ist ein österreichisches Gesetz, das die Regelungen für den Straßenverkehr festlegt. Es betrifft die Zulassung von Fahrzeugen, Führerscheinregelungen, Verkehrsregeln und Haftpflichtversicherungen. Das KFG zielt darauf ab, die Sicherheit im Straßenverkehr zu gewährleisten und den ordnungsgemäßen Betrieb von Fahrzeugen zu regeln.',
    20011502:
      'Das Allgemeine Sozialversicherungsgesetz (ASVG) ist ein österreichisches Gesetz, das die gesetzliche Kranken-, Unfall- und Pensionsversicherung regelt. Es betrifft Beitragspflicht, Leistungsansprüche und Verwaltungsvorschriften für Versicherte. Das ASVG zielt darauf ab, den sozialen Schutz und die Absicherung der Arbeitnehmer in Österreich sicherzustellen.',
    10001848:
      'Das Urheberrechtsgesetz (UrhG) ist ein österreichisches Gesetz zum Schutz von geistigem Eigentum. Es betrifft literarische, musikalische, künstlerische und softwarebezogene Werke. Das UrhG regelt die Rechte der Urheber und die Nutzung ihrer Werke. Es dient dem Schutz des geistigen Eigentums und der Förderung kultureller Vielfalt.',
    10003940:
      'Die Bundesabgabenordnung (BAO) ist ein österreichisches Gesetz, das das Abgabenrecht regelt. Es betrifft die Festsetzung, Erhebung und Einbringung von Steuern und anderen Abgaben. Die BAO enthält Bestimmungen zum Abgabenverfahren, zur Abgabenerklärung und zur Abgabenkontrolle. Sie dient der ordnungsgemäßen Verwaltung und Durchsetzung der Abgaben.',
    10008840:
      'Das Arbeitsverfassungsgesetz (ArbVG) regelt die Rechte und Pflichten von Arbeitnehmern und Arbeitgebern in Österreich. Es umfasst Themen wie Mitbestimmung, Betriebsräte, Arbeitszeit, Urlaub und Kündigungsschutz. Das Ziel des ArbVG ist ein ausgewogenes Verhältnis zwischen Arbeitnehmern und Arbeitgebern sowie der Schutz der Arbeitnehmerrechte. Es bildet die Grundlage für die Arbeitsbeziehungen in Österreich.',
    10008163:
      'Das Gehaltsgesetz (GehG) regelt die Vergütung von Arbeitnehmern in Österreich. Es legt Mindestlöhne, Tarifverträge und Entgeltbestandteile wie Zulagen fest. Das Ziel des GehG ist faire Bezahlung und der Schutz der Arbeitnehmerinteressen. Es bildet die Grundlage für gerechte Entlohnung in Österreich.',
    10008872:
      'Das Arbeitsvertragsrechts-Anpassungsgesetz (AVRAG) regelt die Rechte und Pflichten von Arbeitgebern und Arbeitnehmern in Österreich. Es betrifft Arbeitsverträge, Arbeitszeit, Urlaub und Kündigungsschutz. Das AVG zielt darauf ab, faire Arbeitsbedingungen zu gewährleisten und den Schutz der Arbeitnehmerrechte sicherzustellen. Es bildet die Grundlage für das Arbeitsverhältnis in Österreich.',
    20003047:
      'Das Außerstreitgesetz (AußStrG) regelt außergerichtliche Verfahren in Österreich. Es betrifft Streitigkeiten außerhalb der Zuständigkeit der ordentlichen Gerichte, wie etwa familienrechtliche oder erbschaftsrechtliche Angelegenheiten. Das AußStrG fördert die außergerichtliche Streitbeilegung und ermöglicht eine effiziente Klärung von Rechtsfragen. Es bietet einen Rahmen für schnelle und faire Lösungen außerhalb des Gerichtsverfahrens.',
    10009600:
      'Das Schulunterrichtsgesetz (SchUG) ist ein österreichisches Gesetz, das den Schulunterricht in Österreich regelt. Es betrifft die Organisation der Schulen, die Rechte und Pflichten von Schülern, Lehrern und Eltern sowie die Inhalte und Ziele des Unterrichts. Das SchUG stellt sicher, dass der Schulunterricht strukturiert, qualitativ hochwertig und den Bedürfnissen der Schüler entspricht. Es trägt zur Förderung des Bildungsrechts und zur Gewährleistung einer angemessenen Bildung in Österreich bei.',
    10001720:
      'Es handelt sich um ein österreichisches Bundesgesetz, das die rechtlichen Bestimmungen für GmbHs in Österreich regelt. Es umfasst Regelungen zur Gründung, Organisation, Geschäftsführung, Haftung und Kapitalstruktur von GmbHs. Das GmbHG dient dazu, den rechtlichen Rahmen für GmbHs in Österreich festzulegen und den Schutz der Interessen der Gesellschafter und Gläubiger zu gewährleisten.',
    10008115:
      'Das Vertragsbedienstetengesetz (VBG) regelt die Arbeitsbedingungen und Pflichten von Vertragsbediensteten im österreichischen öffentlichen Dienst. Es betrifft Einstellung, Beförderung, Dienstverhältnisende und stellt sicher, dass die Rechte und Pflichten der Vertragsbediensteten gewahrt werden. Es trägt zur effizienten Verwaltung des öffentlichen Dienstes bei.',
    20001921:
      'Das Wohnungseigentumsgesetz (WEG) von 2002 ist ein österreichisches Gesetz, das die rechtlichen Bestimmungen für das Wohnungseigentum regelt. Es betrifft Eigentumsrechte, Verwaltung, Beschlüsse, Instandhaltung und Streitbeilegung. Das WEG zielt darauf ab, klare Regeln für Wohnungseigentümer zu schaffen und ein harmonisches Zusammenleben in Wohnanlagen zu gewährleisten.',
    10005770:
      'Das Verwaltungsstrafgesetz (VStG) ist ein österreichisches Gesetz, das die Bestimmungen für die Verhängung von Verwaltungsstrafen regelt. Es legt fest, wie Verwaltungsübertretungen behandelt werden, einschließlich Zuständigkeiten, Verfahrensabläufen und Rechtsmitteln. Das VStG hat das Ziel, die Einhaltung behördlicher Vorschriften sicherzustellen und Verstöße angemessen zu ahnden.',
    20001474:
      'Das Kinderbetreuungsgeldgesetz (KBGG) von 2023 regelt das Kinderbetreuungsgeld in Österreich. Es betrifft Anspruchsvoraussetzungen, Zahlungshöhe, -dauer und weitere Unterstützungsleistungen für Eltern. Das KBGG zielt darauf ab, Familien bei der Vereinbarkeit von Beruf und Familie zu unterstützen und finanzielle Sicherheit während der Betreuungszeit zu gewährleisten. Es fördert auch die frühkindliche Entwicklung.',
    10002462:
      'Das Konsumentenschutzgesetz (KSchG) schützt Verbraucher in Österreich. Es regelt Vertragsabschlüsse, Gewährleistung und bekämpft unlautere Geschäftspraktiken. Das Ziel des KSchG ist der Schutz der Verbraucherrechte und ein faires Verhältnis zwischen Verbrauchern und Unternehmen. Es enthält Mechanismen zur Durchsetzung der Verbraucherrechte.',
  },
  courts: {
    ogh: 'Der Oberste Gerichtshof (OGH) ist das höchste Gericht in Österreich. Er entscheidet in Zivil-, Straf- und Verwaltungssachen und sorgt für einheitliche Rechtsprechung. Seine bindenden Entscheidungen müssen von allen niedrigeren Gerichten befolgt werden, wodurch Gerechtigkeit und Rechtsstaatlichkeit gewährleistet werden. Der OGH trägt zur Auslegung und Anwendung des österreichischen Rechts bei.',
    'ausl-egmr':
      'Der Europäische Gerichtshof für Menschenrechte (EGMR) ist ein internationaler Gerichtshof mit Sitz in Straßburg. Er entscheidet über Beschwerden von Einzelpersonen, Organisationen und Staaten bezüglich Verletzungen der Europäischen Menschenrechtskonvention (EMRK). Der EGMR überprüft, ob die Rechte und Freiheiten gemäß der EMRK verletzt wurden. Individuen können eine Beschwerde einreichen, nachdem sie alle innerstaatlichen Rechtsmittel erschöpft haben. Die Urteile des EGMR sind bindend und tragen zur Förderung und zum Schutz der Menschenrechte in Europa bei.',
    'olg-wien':
      'Das OLG Wien ist ein Gericht, das über Berufungen in Zivil- und Strafsachen entscheidet. Es überprüft Urteile der unteren Gerichte in Wien und Niederösterreich. Das OLG Wien trägt dazu bei, dass eine einheitliche Rechtsprechung gewährleistet wird und dass Fehlentscheidungen überprüft werden können.',
    'ausl-bgh':
      'Der Bundesgerichtshof (BGH) ist das höchste Gericht in Deutschland für Zivil- und Strafsachen. Er überprüft Urteile anderer Gerichte und stellt sicher, dass das geltende Recht korrekt angewendet wird. Seine Entscheidungen sind für alle anderen Gerichte in Deutschland verbindlich. Der BGH trägt zur einheitlichen Auslegung des Rechts bei und spielt eine wichtige Rolle für die Gerechtigkeit im deutschen Rechtssystem.',
    'ausl-bag':
      'Das Bundesarbeitsgericht (BAG) ist das höchste deutsche Gericht für Arbeitsrecht. Es ist zuständig für die Entscheidung von Rechtsstreitigkeiten zwischen Arbeitnehmern und Arbeitgebern. Das BAG überprüft Urteile der untergeordneten Arbeitsgerichte und stellt sicher, dass das Arbeitsrecht korrekt angewendet wird. Seine Entscheidungen haben eine wegweisende Bedeutung für die Arbeitsrechtsprechung in Deutschland. Das BAG trägt dazu bei, faire Arbeitsbedingungen und den Schutz der Rechte von Arbeitnehmern und Arbeitgebern zu gewährleisten.',
    rg: 'Das Reichsgericht war das oberste Gericht des Deutschen Kaiserreichs von 1879 bis 1945. Es hatte seinen Sitz in Leipzig und war für die Entscheidung in Zivil- und Strafsachen zuständig.',
    'ausl-ekmr':
      'Die EKMR, auch bekannt als EMRK, ist ein völkerrechtlicher Vertrag zum Schutz der Menschenrechte in Europa. Sie legt grundlegende Rechte und Freiheiten fest. Der Europäische Gerichtshof für Menschenrechte überwacht die Einhaltung der Konvention durch die Mitgliedsstaaten und entscheidet über Beschwerden. Die EKMR spielt eine wichtige Rolle im Schutz der Menschenrechte in Europa.',
    opms: 'Der Oberste Patent- und Markensenat (OPMS oder OPM) ist ein spezialisierter Gerichtssenat in Österreich, der über Patent- und Markenrechtsfragen entscheidet. Seine Urteile haben maßgeblichen Einfluss auf die Rechtsprechung und schaffen Rechtssicherheit im Bereich Patente und Marken.',
    bfg: 'Das Bundesfinanzgericht (BFG) entscheidet über Beschwerden gegen Bescheide eines Finanzamtes, des Zollamtes oder des Amtes für Betrugsbekämpfung in Steuer-, Beihilfen- oder Finanzstrafsachen sowie gegen Bescheide betreffend Wiener Landes- und Gemeindeabgaben (beispielsweise Abfallwirtschaftsgesetz und Vergnügungssteuer) und die abgabenrechtlichen Verwaltungsübertretungen zu diesen Abgaben (beispielsweise Parkometerabgabe). (https://www.bfg.gv.at/)',
    dsb: 'Die Datenschutzbehörde sorgt für die Einhaltung des Datenschutzes in Österreich. (https://www.dsb.gv.at/)',
  },
}

export function getPathMappingsByJurisdiction(
  jurisdiction: Jurisdiction,
): IMapping {
  const filtered: IMapping = {}
  Object.entries(pathMappings).forEach(([categoryKey, category]) => {
    const paths = Object.entries(category.paths).reduce<IPaths>(
      (acc, [key, value]) => {
        const pathJurisdiction =
          value.jurisdiction ?? (categoryKey === 'eu-recht' ? 'eu' : 'at')
        if (pathJurisdiction === jurisdiction) {
          acc[key] = value
        }
        return acc
      },
      {},
    )
    if (Object.keys(paths).length > 0) {
      filtered[categoryKey] = { ...category, paths }
    }
  })
  return filtered
}
